import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { medicalHistoryService } from '../../../../services/medicalHistoryInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('MedicalHistoryInput');

type MedicalHistoryInputScreenRouteProp = RouteProp<RootStackParamList, 'MedicalHistoryInput'>;

export const MedicalHistoryInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<MedicalHistoryInputScreenRouteProp>();

  const { medicalHistoryData, isEditing } = route.params || {};

  // Helper function to format date as MM/DD/YYYY
  const formatDate = (text: string) => {
    // Remove all non-numeric characters
    const cleaned = text.replace(/\D/g, '');

    // Format based on length
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    if (cleaned.length <= 8) return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;

    // Limit to 8 digits (MMDDYYYY)
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
  };

  // Helper function to convert MM/DD/YYYY to ISO 8601 (YYYY-MM-DDTHH:mm:ss.sssZ)
  const convertToISODate = (dateString: string): string => {
    if (!dateString || dateString.length !== 10) return '';

    const [month, day, year] = dateString.split('/');
    if (!month || !day || !year) return '';

    // Create a date object and return in full ISO format
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toISOString();
  };

  // Helper function to convert ISO date to display format (MM/DD/YYYY)
  const convertFromISODate = (isoDate: string): string => {
    if (!isoDate) return '';

    try {
      // Create date object from ISO string
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return '';

      // Extract month, day, year
      const month = date.getMonth() + 1; // getMonth() returns 0-11
      const day = date.getDate();
      const year = date.getFullYear();

      // Return in MM/DD/YYYY format with zero padding
      return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
    } catch (error) {
      return '';
    }
  };

  const [diagnosisDate, setDiagnosisDate] = useState(
    medicalHistoryData?.diagnosisDate ? convertFromISODate(medicalHistoryData.diagnosisDate) : ''
  );
  const [condition, setCondition] = useState(medicalHistoryData?.condition || '');
  const [treatingPhysician, setTreatingPhysician] = useState(medicalHistoryData?.treatingPhysician || '');
  const [treatmentDescription, setTreatmentDescription] = useState(medicalHistoryData?.treatmentDescription || '');
  const [medications, setMedications] = useState(medicalHistoryData?.medications || '');
  const [recoveryNotes, setRecoveryNotes] = useState(medicalHistoryData?.recoveryNotes || '');
  const [followupRequired, setFollowupRequired] = useState(medicalHistoryData?.followupRequired || '');
  const [saving, setSaving] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedMedicalHistoryId, setSavedMedicalHistoryId] = useState<string | undefined>();
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  // Set toggle to true if editing and any additional fields have data
  useEffect(() => {
    if (isEditing && medicalHistoryData) {
      const hasAdditionalData = medicalHistoryData.treatingPhysician ||
                               medicalHistoryData.treatmentDescription ||
                               medicalHistoryData.medications ||
                               medicalHistoryData.recoveryNotes ||
                               medicalHistoryData.followupRequired;
      setShowAdditionalInfo(!!hasAdditionalData);
    }
  }, [isEditing, medicalHistoryData]);

  const handleSave = async () => {
    // Validation - require at least condition
    if (!condition.trim()) {
      Alert.alert('Error', 'Please enter at least a medical condition');
      return;
    }

    try {
      setSaving(true);

      const medicalHistoryUpdateData = {
        taskId: 8, // Medical History task ID
        diagnosisDate: diagnosisDate.trim() ? convertToISODate(diagnosisDate.trim()) : undefined,
        condition: condition.trim(),
        treatingPhysician: treatingPhysician.trim(),
        treatmentDescription: treatmentDescription.trim(),
        medications: medications.trim(),
        recoveryNotes: recoveryNotes.trim(),
        followupRequired: followupRequired.trim(),
      };

      let savedMedicalHistory;

      if (isEditing && medicalHistoryData?.id) {
        // Update existing record
        savedMedicalHistory = await medicalHistoryService.updateMedicalHistoryInfo(
          medicalHistoryData.id,
          medicalHistoryUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedMedicalHistory?.data || savedMedicalHistory;
        logger.debug('Medical history updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('MedicalHistoryLockUnlock', { medicalHistoryId: updatedData.id });
      } else {
        // Create new record
        savedMedicalHistory = await medicalHistoryService.createMedicalHistoryInfo(medicalHistoryUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedMedicalHistory?.data || savedMedicalHistory;
        logger.debug('Medical history saved successfully with ID:', createdData.id);

        // Store the saved ID for the Done button
        setSavedMedicalHistoryId(createdData.id);

        // Clear the form and reset states for Add Another
        setDiagnosisDate('');
        setCondition('');
        setTreatingPhysician('');
        setTreatmentDescription('');
        setMedications('');
        setRecoveryNotes('');
        setFollowupRequired('');
        setShowAdditionalInfo(false);

        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset saving so buttons are interactive
        setSaving(false);
      }
    } catch (error) {
      logger.error('Error saving medical history:', error);
      Alert.alert('Error', 'Failed to save medical history information. Please try again.');
      setSaving(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setDiagnosisDate('');
    setCondition('');
    setTreatingPhysician('');
    setTreatmentDescription('');
    setMedications('');
    setRecoveryNotes('');
    setFollowupRequired('');
    setShowAddAnotherDone(false);
    setShowAdditionalInfo(false);
    setSaving(false);
  };

  const handleDone = async () => {
    setSaving(false);
    logger.debug('[Done Debug] Using ID:', savedMedicalHistoryId);

    // If we don't have a saved ID, get the most recent medical history entry
    if (!savedMedicalHistoryId) {
      try {
        const response = await medicalHistoryService.getMedicalHistoryInfo(8);
        logger.debug('[Done Debug] API response:', response);

        // Handle response format - check if it's wrapped in a success/data object
        const allMedicalHistory = response?.data || response;
        logger.debug('[Done Debug] Extracted medical history array:', allMedicalHistory);

        const mostRecent = allMedicalHistory && Array.isArray(allMedicalHistory) && allMedicalHistory.length > 0 ? allMedicalHistory[allMedicalHistory.length - 1] : null;
        if (mostRecent) {
          logger.debug('[Done Debug] Using most recent ID:', mostRecent.id);
          navigation.navigate('MedicalHistoryLockUnlock', { medicalHistoryId: mostRecent.id });
        } else {
          logger.debug('[Done Debug] No medical history entries found in array:', allMedicalHistory);
          Alert.alert('Error', 'No medical history information found. Please create an entry first.');
        }
      } catch (error) {
        logger.error('[Done Debug] Error getting medical history:', error);
        Alert.alert('Error', 'Failed to get medical history information.');
      }
    } else {
      navigation.navigate('MedicalHistoryLockUnlock', { medicalHistoryId: savedMedicalHistoryId });
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Medical History Information
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Medical Condition *</Text>
            <TextInput
              style={styles.input}
              value={condition}
              onChangeText={setCondition}
              placeholder="Ex: Diabetes, Hypertension, etc."
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Diagnosis Date</Text>
            <TextInput
              style={styles.input}
              value={diagnosisDate}
              onChangeText={(text) => setDiagnosisDate(formatDate(text))}
              placeholder="MM/DD/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>

          {/* Additional Information Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setShowAdditionalInfo(!showAdditionalInfo)}
            >
              <View style={[styles.toggle, showAdditionalInfo && styles.toggleActive]}>
                <View style={[styles.toggleThumb, showAdditionalInfo && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Add additional information related to your medical history?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              Include details about treating physician, treatment, medications, recovery notes, and follow-up requirements.
            </Text>
          </View>

          {showAdditionalInfo && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Treating Physician</Text>
                <TextInput
                  style={styles.input}
                  value={treatingPhysician}
                  onChangeText={setTreatingPhysician}
                  placeholder="Dr. Smith"
                  placeholderTextColor="#A9A9A9"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Treatment Description</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={treatmentDescription}
                  onChangeText={setTreatmentDescription}
                  placeholder="Description of treatment received"
                  placeholderTextColor="#A9A9A9"
                  multiline
                  numberOfLines={4}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Medications</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={medications}
                  onChangeText={setMedications}
                  placeholder="Current medications for this condition"
                  placeholderTextColor="#A9A9A9"
                  multiline
                  numberOfLines={4}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Recovery Notes</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={recoveryNotes}
                  onChangeText={setRecoveryNotes}
                  placeholder="Notes about recovery progress"
                  placeholderTextColor="#A9A9A9"
                  multiline
                  numberOfLines={4}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Follow-up Required</Text>
                <TextInput
                  style={styles.input}
                  value={followupRequired}
                  onChangeText={setFollowupRequired}
                  placeholder="Follow-up requirements or schedule"
                  placeholderTextColor="#A9A9A9"
                />
              </View>
            </>
          )}

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity
                style={styles.addAnotherButton}
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.doneButton}
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {saving ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSave}
                >
                  <Text style={styles.submitButtonText}>
                    {isEditing ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>


      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
});
